import { createAsyncThunk } from '@reduxjs/toolkit';
import { sendGetRequest, sendPatchRequest } from '../../../api/requestUtil';

type AdminArticleSuggestionListResponse = {
  count: number;
  next: string | null;
  previous: string | null;
  results: ArticleSuggestionType[];
  page_size: number;
};

type PendingCountResponse = {
  pending_count: number;
  latest_created_on: string | null;
};

export const getAdminArticleSuggestionList = createAsyncThunk<
  AdminArticleSuggestionListResponse,
  { page?: number },
  { state: AppState; rejectValue: { detail: string } }
>(
  'adminArticleSuggestions/getList',
  async ({ page = 1 }, { getState, rejectWithValue }) => {
    const state = getState();
    const token = state.user.authToken;
    if (!state.user.isStaff || !token) {
      return rejectWithValue({ detail: 'Permission denied.' });
    }
    try {
      const params = new URLSearchParams({ page: page.toString() });
      return await sendGetRequest(`/api/articles/suggestions/admin/?${params}`, token);
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to fetch article suggestions.';
      return rejectWithValue({ detail: message });
    }
  }
);

export const getAdminArticleSuggestionPendingCount = createAsyncThunk<
  PendingCountResponse,
  void,
  { state: AppState; rejectValue: { detail: string } }
>(
  'adminArticleSuggestions/getPendingCount',
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const token = state.user.authToken;
    if (!state.user.isStaff || !token) {
      return rejectWithValue({ detail: 'Permission denied.' });
    }
    try {
      return await sendGetRequest('/api/articles/suggestions/pending-count/', token);
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to fetch pending count.';
      return rejectWithValue({ detail: message });
    }
  }
);

export const adminApproveArticleSuggestion = createAsyncThunk<
  ArticleSuggestionType,
  number,
  { state: AppState; rejectValue: { detail: string } }
>(
  'adminArticleSuggestions/approve',
  async (suggestionId, { getState, rejectWithValue }) => {
    const state = getState();
    const token = state.user.authToken;
    if (!state.user.isStaff || !token) {
      return rejectWithValue({ detail: 'Permission denied.' });
    }
    try {
      return await sendPatchRequest(
        `/api/articles/suggestions/${suggestionId}/approve/`,
        {},
        token
      );
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to approve suggestion.';
      return rejectWithValue({ detail: message });
    }
  }
);

export const adminRejectArticleSuggestion = createAsyncThunk<
  ArticleSuggestionType,
  number,
  { state: AppState; rejectValue: { detail: string } }
>(
  'adminArticleSuggestions/reject',
  async (suggestionId, { getState, rejectWithValue }) => {
    const state = getState();
    const token = state.user.authToken;
    if (!state.user.isStaff || !token) {
      return rejectWithValue({ detail: 'Permission denied.' });
    }
    try {
      return await sendPatchRequest(
        `/api/articles/suggestions/${suggestionId}/reject/`,
        {},
        token
      );
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to reject suggestion.';
      return rejectWithValue({ detail: message });
    }
  }
);
